import { useEffect } from 'react';

const SITE_ORIGIN = 'https://tutela.land';

export interface DocumentMetaOptions {
  /** Gated or account pages: emit robots noindex,nofollow for the route's lifetime. */
  noindex?: boolean;
  /** Language alternates for a translated pair, e.g. { 'pt-PT': '/pilotos', en: '/pilots' }.
   *  Injects <link rel="alternate" hreflang> tags (plus x-default = the first entry). */
  alternates?: Record<string, string>;
}

/**
 * Per-route <title> + meta description + canonical for the SPA. Google renders
 * JS, so it picks these up per page (the static index.html tags only cover the
 * home route and non-rendering social scrapers). Restores the previous values
 * on unmount so navigating away never leaks one route's meta onto another.
 *
 * Pass `canonicalPath` (e.g. "/ignis", matching the sitemap exactly) to
 * self-canonicalise the route and keep og:url / twitter:url in sync. Without it
 * the static homepage canonical ("https://tutela.land/") applies to every
 * route and Google collapses them all into "/" as duplicates.
 *
 * Pass `{ noindex: true }` on every login-gated surface (consoles, briefing,
 * nowcast, simulação, account pages): the public site explains, the
 * functionality is behind login and must not be indexed (SITE_PLAN section 2).
 */
export function useDocumentMeta(
  title: string,
  description?: string,
  canonicalPath?: string,
  options?: DocumentMetaOptions,
): void {
  const noindex = options?.noindex === true;
  const alternatesKey = options?.alternates ? JSON.stringify(options.alternates) : '';
  useEffect(() => {
    const alternates: Record<string, string> = alternatesKey ? JSON.parse(alternatesKey) : {};
    // Track restores so unmount reverts every attribute we touched.
    const restores: Array<() => void> = [];

    const setAttr = (selector: string, attr: string, value: string): void => {
      const el = document.querySelector(selector);
      if (!el) return;
      const prev = el.getAttribute(attr);
      el.setAttribute(attr, value);
      restores.push(() => {
        if (prev != null) el.setAttribute(attr, prev);
      });
    };

    const prevTitle = document.title;
    document.title = title;
    restores.push(() => {
      document.title = prevTitle;
    });

    if (description) {
      setAttr('meta[name="description"]', 'content', description);
    }

    if (canonicalPath) {
      const url = `${SITE_ORIGIN}${canonicalPath}`;
      setAttr('link[rel="canonical"]', 'href', url);
      setAttr('meta[property="og:url"]', 'content', url);
      setAttr('meta[name="twitter:url"]', 'content', url);
      setAttr('meta[property="og:title"]', 'content', title);
      setAttr('meta[name="twitter:title"]', 'content', title);
    }

    if (noindex) {
      setAttr('meta[name="robots"]', 'content', 'noindex, nofollow');
    }

    const langs = Object.keys(alternates);
    if (langs.length > 0) {
      // Replace the static pt-pt / x-default pair for the lifetime of this route.
      const previous = Array.from(document.querySelectorAll('link[rel="alternate"][hreflang]'));
      previous.forEach((el) => el.parentElement?.removeChild(el));
      const added: HTMLLinkElement[] = [];
      const add = (hreflang: string, path: string) => {
        const el = document.createElement('link');
        el.rel = 'alternate';
        el.hreflang = hreflang;
        el.href = `${SITE_ORIGIN}${path}`;
        document.head.appendChild(el);
        added.push(el);
      };
      langs.forEach((l) => add(l, alternates[l]));
      add('x-default', alternates[langs[0]]);
      restores.push(() => {
        added.forEach((el) => el.parentElement?.removeChild(el));
        previous.forEach((el) => document.head.appendChild(el));
      });
    }

    return () => {
      // Restore in reverse so overlapping updates unwind cleanly.
      for (let i = restores.length - 1; i >= 0; i--) restores[i]();
    };
  }, [title, description, canonicalPath, noindex, alternatesKey]);
}
